import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Droplets, TreeDeciduous, Map, Search, Leaf, Mountain,
  ArrowRight, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { TiltCard } from '../components/effects/TiltCard';
import { WaterRipple } from '../components/effects/WaterRipple';
import { RippleButton } from '../components/effects/RippleButton';
import { ParallaxHero } from '../components/effects/ParallaxHero';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMAGE = "https://images.unsplash.com/photo-1660294535383-434c648ee004?w=1600&q=75&fm=webp";
const PHILOSOPHY_IMAGE = "https://images.unsplash.com/photo-1660294534543-eb2bb92ed40f?w=800&q=75&fm=webp";
const REGIONAL_IMAGE = "https://images.unsplash.com/photo-1652722736772-c48164a98c7a?w=800&q=75&fm=webp";
const GIS_IMAGE = "https://images.unsplash.com/photo-1569163139599-0f4517e36f51?w=800&q=75&fm=webp";

const serviceIcons = {
  "Ecological Land Design": Leaf,
  "Water Systems & Earthworks": Droplets,
  "Forest & Biodiversity Restoration": TreeDeciduous,
  "GIS Landscape Analysis": Map,
  "Farm & Homestead Planning": Mountain,
  "Land Stewardship Consulting": Search,
};

const servicesList = [
  { title: "Ecological Land Design", description: "Designing landscapes that work with natural systems" },
  { title: "Water Systems & Earthworks", description: "Swales, ponds, and rainwater harvesting" },
  { title: "Forest & Biodiversity Restoration", description: "Native forest and food forest creation" },
  { title: "GIS Landscape Analysis", description: "Terrain and watershed mapping" },
  { title: "Farm & Homestead Planning", description: "Productive land use planning" },
  { title: "Land Stewardship Consulting", description: "Ongoing guidance and support" },
];

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

export default function Home() {
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${API}/projects`);
        setProjects(response.data.slice(0, 4));
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    };
    fetchProjects();
  }, []);

  return (
    <main className="min-h-screen" data-testid="home-page">
      {/* Hero Section with Parallax */}
      <ParallaxHero
        imageSrc={HERO_IMAGE}
        imageAlt="Western Ghats misty mountains"
        className="h-screen min-h-[700px] flex items-center justify-center"
        overlayClassName="hero-gradient"
        parallaxIntensity={0.3}
        priority={true}
        data-testid="hero-section"
      >
        <div className="flex items-center justify-center h-full">
          <div className="text-center text-white px-6 max-w-5xl mx-auto">
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-sm md:text-base tracking-[0.3em] uppercase mb-6 text-white/80"
            >
              Ecological Landscape Design
            </motion.p>
            
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-tight mb-8"
              data-testid="hero-headline"
            >
              Ecological Landscape Design<br />
              <span className="italic font-normal">Rooted in Nature</span>
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-base md:text-lg lg:text-xl text-white/90 max-w-3xl mx-auto mb-12 leading-relaxed"
            >
              We design regenerative landscapes that restore water cycles, rebuild soil health, 
              and grow resilient ecosystems for farms, communities, and homesteads.
            </motion.p>
            
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <Link to="/contact">
                <RippleButton 
                  className="btn-primary"
                  data-testid="hero-cta-primary"
                >
                  Start Your Ecological Design Journey
                </RippleButton>
              </Link>
              <Link to="/projects">
                <RippleButton 
                  className="border-2 border-white text-white bg-white/10 backdrop-blur-sm rounded-full px-8 py-4 font-semibold text-sm tracking-wider uppercase hover:bg-white hover:text-forest transition-all duration-300"
                  data-testid="hero-cta-secondary"
                >
                  Explore Our Work
                </RippleButton>
              </Link>
            </motion.div>
          </div>
        </div>
      </ParallaxHero>

      {/* Philosophy Section */}
      <section className="section-padding bg-cream" data-testid="philosophy-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeInUp}
              transition={{ duration: 0.8 }}
            >
              <p className="text-sm tracking-[0.2em] uppercase text-earth mb-4">Our Philosophy</p>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-forest mb-8 leading-tight">
                Designing With Nature,<br />
                <span className="italic">Not Against It</span>
              </h2>
              <div className="space-y-6 text-foreground/80 leading-relaxed">
                <p>
                  Nature has already developed systems that manage water, soil, forests, and biodiversity. 
                  Our role is to observe these systems and guide landscapes toward long-term ecological health.
                </p>
                <p>
                  At F.E.R.A.Lways we design land that becomes more productive, resilient, and alive with each season.
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeInUp}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative overflow-hidden rounded-2xl">
                <img 
                  src={PHILOSOPHY_IMAGE}
                  alt="Road through foggy Western Ghats forest"
                  className="w-full h-[400px] lg:h-[500px] object-cover img-hover"
                  loading="lazy"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-earth/10 rounded-full -z-10" />
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-forest/10 rounded-full -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Preview Section */}
      <WaterRipple className="section-padding bg-sand" data-testid="services-section">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <p className="text-sm tracking-[0.2em] uppercase text-earth mb-4">What We Do</p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-forest">
              Our Services
            </h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {servicesList.map((service, index) => {
              const IconComponent = serviceIcons[service.title] || Leaf;
              return (
                <TiltCard key={index} intensity={10}>
                  <motion.div
                    variants={fadeInUp}
                    className="bg-white/60 backdrop-blur-sm border border-border/40 rounded-2xl p-8 group h-full"
                  >
                    <div className="icon-bg mb-6 group-hover:bg-forest/20">
                      <IconComponent className="w-7 h-7 text-forest" />
                    </div>
                    <h3 className="font-serif text-xl text-forest mb-3">{service.title}</h3>
                    <p className="text-foreground/70 text-sm leading-relaxed">{service.description}</p>
                  </motion.div>
                </TiltCard>
              );
            })}
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mt-12"
          >
            <Link to="/services">
              <RippleButton className="btn-primary" data-testid="view-all-services">
                View All Services <ArrowRight className="ml-2 w-4 h-4" />
              </RippleButton>
            </Link>
          </motion.div>
        </div>
      </WaterRipple>

      {/* Regional Landscape Section */}
      <section className="section-padding bg-cream" data-testid="regional-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeInUp}
              transition={{ duration: 0.8 }}
              className="order-2 lg:order-1 relative"
            >
              <div className="relative overflow-hidden rounded-2xl">
                <img 
                  src={REGIONAL_IMAGE}
                  alt="Lush coffee plantation in Coorg"
                  className="w-full h-[400px] lg:h-[500px] object-cover img-hover"
                  loading="lazy"
                />
              </div>
            </motion.div>
            
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeInUp}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="order-1 lg:order-2"
            >
              <p className="text-sm tracking-[0.2em] uppercase text-earth mb-4">Regional Focus</p>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-forest mb-8 leading-tight">
                Working With the Land of<br />
                <span className="italic">the Western Ghats</span>
              </h2>
              <div className="space-y-6 text-foreground/80 leading-relaxed">
                <p>
                  The landscapes of Karnataka are shaped by monsoon rains, mountain forests, and fertile soils. 
                  These ecosystems hold incredible biodiversity and agricultural potential.
                </p>
                <p>
                  Our work focuses on designing systems that strengthen these natural processes rather than disrupting them.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* GIS Mapping Section */}
      <section className="section-padding bg-forest text-white" data-testid="gis-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeInUp}
              transition={{ duration: 0.8 }}
            >
              <p className="text-sm tracking-[0.2em] uppercase text-white/60 mb-4">Technology</p>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl mb-8 leading-tight">
                Understanding Land<br />
                <span className="italic">Through Mapping</span>
              </h2>
              <div className="space-y-6 text-white/80 leading-relaxed">
                <p>
                  We use Geographic Information Systems (GIS) to analyze elevation, watershed dynamics, 
                  soil patterns, and vegetation systems.
                </p>
                <p>
                  Mapping helps reveal how landscapes function beneath the surface. This allows us to design 
                  water systems, planting strategies, and land use plans that align with natural terrain.
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeInUp}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative overflow-hidden rounded-2xl border border-white/10">
                <img 
                  src={GIS_IMAGE}
                  alt="GIS terrain mapping visualization"
                  className="w-full h-[400px] lg:h-[450px] object-cover opacity-90"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-forest/50 to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Projects Section */}
      <section className="section-padding bg-cream" data-testid="projects-section">
        <div className="container-custom">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <p className="text-sm tracking-[0.2em] uppercase text-earth mb-4">Our Work</p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-forest">
              Featured Projects
            </h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 gap-6 lg:gap-8"
          >
            {projects.map((project, index) => (
              <motion.div
                key={project.id}
                variants={fadeInUp}
              >
                <Link 
                  to={`/projects/${project.id}`}
                  className="group block relative overflow-hidden rounded-2xl h-[350px] lg:h-[400px] card-hover"
                  data-testid={`project-card-${project.id}`}
                >
                  <img 
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 project-overlay" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8">
                    <p className="text-white/70 text-sm mb-2">{project.category}</p>
                    <h3 className="font-serif text-xl lg:text-2xl text-white mb-2">{project.title}</h3>
                    <p className="text-white/70 text-sm">{project.location}</p>
                    <div className="flex items-center mt-4 text-white/80 group-hover:text-white transition-colors">
                      <span className="text-sm">View Project</span>
                      <ChevronRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mt-12"
          >
            <Link to="/projects">
              <Button className="btn-primary" data-testid="view-all-projects">
                View All Projects <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Products Section */}
      <section className="section-padding bg-sand" data-testid="products-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeInUp}
              transition={{ duration: 0.8 }}
            >
              <p className="text-sm tracking-[0.2em] uppercase text-earth mb-4">Shop</p>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-forest mb-8 leading-tight">
                Nature-Inspired<br />
                <span className="italic">Products</span>
              </h2>
              <p className="text-foreground/80 leading-relaxed mb-8">
                Sustainably sourced products from the Western Ghats — artisanal coffee, wild honey, 
                adaptogenic blends, and more. Every purchase supports wilderness revival initiatives 
                and regenerative livelihoods.
              </p>
              <Link to="/store">
                <Button className="btn-primary" data-testid="visit-store">
                  Visit Store <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </motion.div>
            
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeInUp}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="overflow-hidden rounded-2xl bg-white shadow-sm">
                <img 
                  src="https://customer-assets.emergentagent.com/job_ecological-design/artifacts/f033qm6u_Coffee%202.png"
                  alt="Artisanal Coffee"
                  className="w-full h-48 object-cover img-hover"
                  loading="lazy"
                />
              </div>
              <div className="overflow-hidden rounded-2xl mt-8 bg-white shadow-sm">
                <img 
                  src="https://customer-assets.emergentagent.com/job_ecological-design/artifacts/jz20vhxm_Honey%202.png"
                  alt="Jungle Honey"
                  className="w-full h-48 object-cover img-hover"
                  loading="lazy"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="relative py-32 md:py-40 overflow-hidden"
        data-testid="cta-section"
      >
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1708593343700-a101f8fe4d11?w=800&q=75&fm=webp"
            alt="Forest restoration"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-forest/85" />
        </div>
        
        <div className="relative z-10 container-custom px-6 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white mb-8 max-w-3xl mx-auto leading-tight">
              Design Your Land With<br />
              <span className="italic">Ecological Intelligence</span>
            </h2>
            <p className="text-white/80 text-lg max-w-2xl mx-auto mb-12 leading-relaxed">
              Healthy landscapes regulate water, build soil, support biodiversity, and grow food. 
              Let's design a system that works with nature and strengthens your land for generations.
            </p>
            <Link to="/contact">
              <Button 
                className="bg-white text-forest hover:bg-white/90 rounded-full px-10 py-5 font-semibold text-sm tracking-wider uppercase transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                data-testid="cta-book-consultation"
              >
                Book a Consultation
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
